import {Heading, Icon} from '@albert-io/atomic';
import classNames from 'classnames';
import React, {useId, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useQuestionTranslationStore, PromptText} from '../TranslationSection';

import {Prompt} from './ContentBlocks';

type CollapsiblePromptProps = React.ComponentProps<typeof Prompt>;

/**
 * The question prompt under a "Prompt" heading that collapses it, for a teacher reading a
 * student's submission alongside the grading panel. The caret stays hidden until the header
 * is hovered or focused, and stays visible while collapsed.
 */
export const CollapsiblePrompt = ({question, variant}: CollapsiblePromptProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const promptId = useId();
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  return (
    // `Prompt` brings its own bottom margin, which goes away with it when collapsed.
    <div className={classNames({'mb-x4': isCollapsed})}>
      <Heading as='h2' size='s' className='mt-x0 mb-x2'>
        <button
          type='button'
          aria-expanded={!isCollapsed}
          aria-controls={promptId}
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          // There's no preflight, so the browser's own button chrome, font size and weight
          // all have to be cleared by hand.
          className='group p-x0 flex w-full cursor-pointer items-center border-0 bg-transparent text-left text-xl font-black'
          data-testid='collapsible-prompt-toggle'
        >
          <span className='relative'>
            {/* An x4 slot out of flow, so the caret centres in the question body's padding the way
                the design's icon button does: the heading stays lined up with every other section,
                and revealing the caret never shifts the title. Callers setting `collapsiblePrompt`
                hold that padding at x4 to leave the slot room. */}
            <span
              className={classNames(
                'right-full w-x4 absolute top-1/2 flex -translate-y-1/2 justify-center',
                {'invisible group-hover:visible group-focus-visible:visible': !isCollapsed}
              )}
            >
              <Icon icon={isCollapsed ? 'caret-right' : 'caret-down'} />
            </span>
            {showTranslations ? PromptText : 'Prompt'}
          </span>
        </button>
      </Heading>
      <div id={promptId} hidden={isCollapsed}>
        <Prompt question={question} variant={variant} />
      </div>
    </div>
  );
};

export default CollapsiblePrompt;
